import _ from "lodash";
import client from "lib/apolloClient/client";
import { generateRandomId } from "Utils";
import {
  writeRubricElementInCache,
  writeOrganizationRubricsInCache,
  getOrganizationRubricsFromCache,
} from "./RubricGraphqlHelpers";
import { getOrganizationRubricsQuery } from "./RubricQuery";
import {
  createRubricIndicatorMutation,
  createRubricCriteriaMutation,
  updateRubricIndicatorMutation,
  updateRubricCriteriaMutation,
  updateRubricDescriptorMutation,
  deleteRubricIndicatorMutation,
  deleteRubricCriteriaMutation,
  createRubricMutation,
  deleteRubricMutation,
  bulkUpdateRubricDescriptorsMutation,
  bulkCreateRubricIndicatorsMutation,
} from "./RubricMutation";
import {
  writeAssessmentToolNodeDetailsInCache,
  getOrganizationConstantsFromCache,
  getStaffBasicDetailsFromCache,
  getAssessmentToolNodeDetailsFromCacheV2,
} from "modules/CommonGraphqlHelpers";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { writeAssessmentToolOfParentInCache } from "modules/AssessmentTool/AssessmentToolGraphqlHelpers";
import { getUpdatedAssessmentToolDetails } from "modules/AssessmentTool/AssessmentToolModule";
import {
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { insertElementInAssessmentTool } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils";

export const saveRubric = ({
  copyToolId,
  mode = "CREATE",
  isGlobal = true,
  title = "",
  parentId,
  parentType,
  filters,
  curriculumProgramId,
  extraParams = {},
  portalType,
  shouldRefetchQuery = false,
} = {}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    const state = getState();
    const rubricId = copyToolId;
    const copyRubric = mode == "COPY";
    const organizationId = userInfo.org_id;
    const academicYearId = getSelectedAcademicYearId(state);

    const organizationConstants = getOrganizationConstantsFromCache(
      organizationId
    );
    const rubricLevels =
      _.get(extraParams, "criterias") ||
      _.get(organizationConstants, "rubricLevels", []);

    const organizationDeatils = getOrganizationRubricsFromCache({
      id: organizationId,
      filters,
    });

    const rubrics = _.get(organizationDeatils, "rubrics", []) || [];
    const criterias = _.map(rubricLevels, level => {
      return {
        label: level,
        id: generateRandomId(),
        __typename: "CriteriaRubric",
      };
    });

    const indicators = [];

    if (_.has(extraParams, "indicators")) {
      indicators.push(
        ..._.map(extraParams.indicators, indicator => {
          return {
            id: generateRandomId(),
            label: indicator.label,
            __typename: "IndicatorRubric",
          };
        })
      );
    } else {
      indicators.push({
        label: "",
        id: generateRandomId(),
        __typename: "IndicatorRubric",
      });
    }

    const descriptors = _.map(criterias, obj => {
      return {
        id: generateRandomId(),
        label: "",
        indicator: indicators[0],
        criteria: obj,
        __typename: "DescriptorRubric",
      };
    });

    let rubricData = getAssessmentToolNodeDetailsFromCacheV2({
      id: rubricId,
      type: "RUBRIC",
    });

    if (!copyRubric) {
      rubricData = {
        id: generateRandomId(),
        __typename: "Rubric",
        title,
        indicators,
        criterias,
        descriptors,
        createdBy: getStaffBasicDetailsFromCache(userInfo.id),
      };
    } else {
      rubricData = {
        ...rubricData,
        id: generateRandomId(),
        title,
        createdBy: getStaffBasicDetailsFromCache(userInfo.id),
      };
    }

    // If true, default criterias will be used in BE. No need to send from FE (except for AI)
    const useDefaultCriteria = _.isEmpty(_.get(extraParams, "criterias", []));

    const refetchQueries = [];
    if (shouldRefetchQuery) {
      refetchQueries.push({
        query: getOrganizationRubricsQuery,
        variables: {
          id: organizationId,
          filters,
        },
      });
    }

    try {
      const result = await client.mutate({
        mutation: createRubricMutation,
        variables: {
          createdBy: userInfo.id,
          criterias: useDefaultCriteria
            ? undefined
            : _.map(criterias, criteria => _.pick(criteria, "label")),
          indicators: _.map(indicators, indicator =>
            _.pick(indicator, "label")
          ),
          title,
          copyRubric,
          rubricId,
          isGlobal,
          parentId,
          parentType,
          portalType,
          curriculumProgramId:
            isGlobal || useDefaultCriteria
              ? curriculumProgramId || getSelectedCurriculumId(getState())
              : undefined,
        },
        awaitRefetchQueries: shouldRefetchQuery,
        refetchQueries,
        // optimisticResponse: {
        //   __typename: "Mutation",
        //   planner: {
        //     __typename: "PlannerMutations",
        //     createRubric: rubricData
        //   }
        // },
        update: (
          cache,
          {
            data: {
              planner: { createRubric: createRubricMutationResponse },
            },
          }
        ) => {
          if (!_.isEmpty(createRubricMutationResponse)) {
            if (isGlobal && !shouldRefetchQuery) {
              // isGlobal is true when you save tool as template
              const data = {
                ...organizationDeatils,
                rubrics: [...rubrics, createRubricMutationResponse],
              };

              setTimeout(() =>
                writeOrganizationRubricsInCache({
                  id: organizationId,
                  data,
                  filters,
                })
              );
            }
            if (parentId) {
              const assessmentToolDetails = dispatch(
                getUpdatedAssessmentToolDetails({
                  parentId,
                  parentType,
                  mutationResponse: createRubricMutationResponse,
                  assessmentToolType: "RUBRIC",
                  extraParams,
                })
              );

              setTimeout(() =>
                writeAssessmentToolOfParentInCache({
                  id: parentId,
                  data: assessmentToolDetails,
                  type: parentType,
                  academicYearId,
                })
              );
            }
          }
        },
      });

      // show toast only when user save tool as template
      if (mode === "COPY" && isGlobal) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:label_successfully_duplicated",
            locale_params: [
              { key: "label", value: "common:rubric", isPlainText: false },
            ],
            type: "success",
          })
        );
      }
      return _.get(result, "data.planner.createRubric.id", null);
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
      return null;
    }
  };
};

export const updateRubricElement = ({ id, label, type, portalType }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    try {
      let mutation = "";
      switch (type) {
        case "DESCRIPTOR":
          mutation = updateRubricDescriptorMutation;
          break;
        case "INDICATOR":
          mutation = updateRubricIndicatorMutation;
          break;
        case "CRITERIA":
          mutation = updateRubricCriteriaMutation;
          break;
      }

      await client.mutate({
        mutation,
        variables: {
          label,
          updatedBy: userInfo.id,
          id,
          portalType,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const writeRubricElementLocal = ({
  id,
  type,
  label,
  elementDetails,
}) => {
  return (dispatch, getState) => {
    const updatedElementDetails = { ...elementDetails, label };
    writeRubricElementInCache({ id, type, data: updatedElementDetails });
  };
};

export const createRubricIndicator = ({
  rubricId,
  portalType,
  putAfterItemId,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    try {
      const rubricData = getAssessmentToolNodeDetailsFromCacheV2({
        id: rubricId,
        type: "RUBRIC",
      });
      const indicators = _.get(rubricData, "indicators", []);
      if (_.isUndefined(putAfterItemId)) {
        // to handle old flow where putAfterItemId does not exist
        putAfterItemId = _.get(_.last(indicators), "id", null);
      }
      const putAfterItemIndex = _.findIndex(indicators, {
        id: putAfterItemId,
      });

      const newIndicator = {
        __typename: "IndicatorRubric",
        id: generateRandomId(),
        label: "",
        displaySequence: putAfterItemIndex + 1,
      };

      const criterias = _.get(rubricData, "criterias", []);

      const newDescriptors = _.map(criterias, criteria => {
        return {
          id: generateRandomId(),
          label: "",
          __typename: "DescriptorRubric",

          indicator: newIndicator,
          criteria,
        };
      });

      await client.mutate({
        mutation: createRubricIndicatorMutation,
        variables: {
          label: "",
          createdBy: userInfo.id,
          rubricId,
          portalType,
          putAfterItemId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            createRubricIndicator: {
              __typename: "IndicatorRow",
              indicator: newIndicator,
              descriptors: newDescriptors,
            },
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { createRubricIndicator },
            },
          }
        ) => {
          let indicators = _.get(rubricData, "indicators", []);
          indicators = insertElementInAssessmentTool({
            putAfterItemId,
            assessmentTool: indicators,
            elementToAdd: createRubricIndicator.indicator,
          });

          let descriptors = _.get(rubricData, "descriptors", []);
          if (!descriptors) {
            descriptors = [];
          }
          descriptors = [...descriptors, ...createRubricIndicator.descriptors];
          const data = {
            ...rubricData,
            indicators,
            descriptors,
          };

          setTimeout(() =>
            writeAssessmentToolNodeDetailsInCache({
              id: rubricId,
              data,
              type: "RUBRIC",
            })
          );
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const createRubricCriteria = ({
  rubricId,
  portalType,
  putAfterItemId,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    try {
      const rubricData = getAssessmentToolNodeDetailsFromCacheV2({
        id: rubricId,
        type: "RUBRIC",
      });
      const criterias = _.get(rubricData, "criterias", []);
      if (_.isUndefined(putAfterItemId)) {
        // to handle old flow where putAfterItemId does not exist
        putAfterItemId = _.get(_.last(criterias), "id", null);
      }
      const putAfterItemIndex = _.findIndex(criterias, {
        id: putAfterItemId,
      });
      const newCriteria = {
        __typename: "CriteriaRubric",
        id: generateRandomId(),
        label: "",
        displaySequence: putAfterItemIndex + 1,
      };

      const indicators = _.get(rubricData, "indicators", []);
      const newDescriptors = _.map(indicators, indicator => {
        return {
          id: generateRandomId(),
          label: "",
          __typename: "DescriptorRubric",

          indicator,
          criteria: newCriteria,
        };
      });

      await client.mutate({
        mutation: createRubricCriteriaMutation,
        variables: {
          label: "",
          createdBy: userInfo.id,
          rubricId,
          portalType,
          putAfterItemId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            createRubricCriteria: {
              __typename: "CriteriaColumn",
              criteria: newCriteria,
              descriptors: newDescriptors,
            },
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { createRubricCriteria },
            },
          }
        ) => {
          let criterias = _.get(rubricData, "criterias", []);
          criterias = insertElementInAssessmentTool({
            putAfterItemId,
            assessmentTool: criterias,
            elementToAdd: createRubricCriteria.criteria,
          });

          let descriptors = _.get(rubricData, "descriptors", []);
          if (!descriptors) {
            descriptors = [];
          }
          descriptors = [...descriptors, ...createRubricCriteria.descriptors];

          const data = {
            ...rubricData,
            criterias,
            descriptors,
          };

          setTimeout(() =>
            writeAssessmentToolNodeDetailsInCache({
              id: rubricId,
              data,
              type: "RUBRIC",
            })
          );
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const deleteRubric = ({ id, filters, showDiscardToast = false }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const organizationId = userInfo.org_id;

    const organizationDeatils = getOrganizationRubricsFromCache({
      id: organizationId,
      filters,
    });

    const rubrics = _.get(organizationDeatils, "rubrics", []);
    try {
      await client.mutate({
        mutation: deleteRubricMutation,
        variables: {
          id: id,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteRubric: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteRubric },
            },
          }
        ) => {
          if (deleteRubric) {
            const updatedRubrics = _.filter(rubrics, rub => rub.id != id);
            const data = {
              ...organizationDeatils,
              rubrics: updatedRubrics,
            };

            setTimeout(() =>
              writeOrganizationRubricsInCache({
                id: organizationId,
                data,
                filters,
              })
            );
          }
        },
      });
      dispatch(
        setToastMsg({
          msg: showDiscardToast
            ? "toastMsgs:label_successfully_discarded"
            : "toastMsgs:label_successfully_deleted",
          type: "success",
          locale_params: [
            { key: "label", value: "common:rubric", isPlainText: false },
          ],
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      setTimeout(() =>
        writeOrganizationRubricsInCache({
          id: organizationId,
          data: organizationDeatils,
          filters,
        })
      );
      console.error(e);
    }
  };
};

export const deleteRubricIndicator = ({ id, rubricId, portalType }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    try {
      await client.mutate({
        mutation: deleteRubricIndicatorMutation,
        variables: {
          id: id,
          deletedBy: userInfo.id,
          rubricId,
          portalType,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteRubricIndicator: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteRubricIndicator },
            },
          }
        ) => {
          if (deleteRubricIndicator) {
            let rubricData = getAssessmentToolNodeDetailsFromCacheV2({
              id: rubricId,
              type: "RUBRIC",
            });
            let indicators = _.get(rubricData, "indicators", []);

            indicators = _.filter(indicators, obj => obj.id != id);
            let descriptors = _.get(rubricData, "descriptors", []);
            descriptors = _.filter(descriptors, obj => obj.indicator.id != id);
            rubricData = {
              ...rubricData,
              indicators,
              descriptors,
            };
            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: rubricId,
                data: rubricData,
                type: "RUBRIC",
              })
            );
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const deleteRubricCriteria = ({ id, rubricId, portalType }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    try {
      await client.mutate({
        mutation: deleteRubricCriteriaMutation,
        variables: {
          id: id,
          deletedBy: userInfo.id,
          rubricId,
          portalType,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteRubricCriteria: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteRubricCriteria },
            },
          }
        ) => {
          if (deleteRubricCriteria) {
            let rubricData = getAssessmentToolNodeDetailsFromCacheV2({
              id: rubricId,
              type: "RUBRIC",
            });
            let criterias = _.get(rubricData, "criterias", []);

            criterias = _.filter(criterias, obj => obj.id != id);
            let descriptors = _.get(rubricData, "descriptors", []);
            descriptors = _.filter(descriptors, obj => obj.criteria.id != id);
            rubricData = {
              ...rubricData,
              criterias,
              descriptors,
            };
            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: rubricId,
                data: rubricData,
                type: "RUBRIC",
              })
            );
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const bulkUpdateRubricDescriptors = params => {
  return async (dispatch, getState) => {
    const { descriptors = [] } = params;
    const userInfo = getUserInfo();

    const input = _.map(descriptors, descriptor => {
      return {
        ...descriptor,
        updatedBy: userInfo.id,
      };
    });

    try {
      await client.mutate({
        mutation: bulkUpdateRubricDescriptorsMutation,
        variables: {
          input,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const bulkCreateRubricIndicators = params => {
  return async (dispatch, getState) => {
    const { indicators = [] } = params;
    const userInfo = getUserInfo();

    const input = _.map(indicators, indicator => {
      return {
        ...indicator,
        createdBy: userInfo.id,
      };
    });

    try {
      const response = await client.mutate({
        mutation: bulkCreateRubricIndicatorsMutation,
        variables: {
          input,
        },
        update: (
          cache,
          {
            data: {
              planner: { bulkCreateRubricIndicators },
            },
          }
        ) => {
          const rubricId = _.get(_.first(indicators), "rubricId");
          const response = _.get(bulkCreateRubricIndicators, "response", []);

          if (!_.isEmpty(response)) {
            const rubric = getAssessmentToolNodeDetailsFromCacheV2({
              id: rubricId,
              type: "RUBRIC",
            });

            const indicators = _.get(rubric, "indicators", []);
            const flatIndicators = _.flatMap(response, "indicator", []);

            const descriptors = _.get(rubric, "descriptors", []);
            const flatDescriptors = _.flatMap(response, "descriptors", []);

            const updatedIndicators = [...indicators, ...flatIndicators];
            const updatedDescriptors = [...descriptors, ...flatDescriptors];

            const updatedRubric = {
              ...rubric,
              indicators: updatedIndicators,
              descriptors: updatedDescriptors,
            };

            writeAssessmentToolNodeDetailsInCache({
              id: rubricId,
              data: updatedRubric,
              type: "RUBRIC",
            });
          }
        },
      });

      return _.get(
        response,
        "data.planner.bulkCreateRubricIndicators.response",
        []
      );
    } catch (e) {
      console.error(e);
    }
  };
};

const REDUCER_HANDLERS = {};

const initialState = {};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
